class BodyPillowCaseBlock extends HTMLElement {
  constructor() {
    super();
    this.products = this.getBodyPillowCaseData();
    this.cartDrawer = document.querySelector("cart-drawer");
    this.cartCountBubble = document.querySelector("#cart-icon-bubble");
    this.shopliftSubscriptionAllowed =
      this.dataset.subscriptionAllowed !== "false";
  }

  /**
   * Initializes DOM references, event listeners, default selections, and renders initial state on mount.
   */
  connectedCallback() {
    // Size selector
    this.size = this.querySelector("#pillow-case-size-selector");
    this.sizeLabel = this.size.querySelector(
      "#pillow-case-size-selector-label"
    );

    // Color selector
    this.color = this.querySelector("#pillow-case-color-selector");
    this.colorLabel = this.querySelector("#pillow-case-color-selector-label");

    // Action Buttons
    this.actionButtons = this.querySelector("#pillow-case-action-buttons");
    this.atcBtn = this.actionButtons.querySelector(
      "#body-pillow-case-block--atc"
    );
    this.klaviyoBtn = this.querySelector("#klaviyo-btn");
    this.unavailableVariantBtn = this.querySelector("#unavailable-variant-btn");

    // Suggestion message for unavailable variant
    this.suggestionMessageForUnavailableVariant = this.querySelector(
      "#suggestion-message-for-unavailable-variant"
    );

    // Price label
    this.price = this.atcBtn.querySelector(
      "#body-pillow-case-block--atc-price"
    );

    // Selling plan toggle
    this.sellingPlanToggle = this.querySelector(
      "#pillow-case-selling-plan-toggle"
    );
    this.sellingPlanRadios = this.querySelectorAll(
      'input[name="pillow-case-selling-plan"]'
    );
    this.subscriptionPriceLabel = this.querySelector(
      "#selling-plan-subscription-price"
    );
    this.onetimePriceLabel = this.querySelector("#selling-plan-onetime-price");
    this.currentSellingPlanId = null;

    // Event listeners
    this.size.addEventListener("input", this.sizeEventHandler.bind(this));
    this.color.addEventListener("input", this.colorEventHandler.bind(this));

    this.atcBtn.addEventListener("click", this.atcBtnEventHandler.bind(this));
    this.klaviyoBtn.addEventListener(
      "click",
      this.dispatchKlaviyoSubscriptionData.bind(this)
    );
    this.sellingPlanRadios.forEach((radio) => {
      radio.addEventListener(
        "change",
        this.sellingPlanChangeHandler.bind(this)
      );
    });

    this.setDefaultSize();

    this.loadCurrentProduct();

    this.updateProducts();

    this.actionButtons.classList.remove("loading-data");
  }

  /**
   * Cleans up listeners when the element is removed from the DOM.
   */
  disconnectedCallback() {
    this.size.removeEventListener("input", this.sizeEventHandler.bind(this));
    this.color.removeEventListener("input", this.colorEventHandler.bind(this));
    this.actionButtons.removeEventListener(
      "click",
      this.actionButtonsEventHandler.bind(this)
    );
  }

  /**
   * Initializes the block with the current product's default variant,
   */
  loadCurrentProduct() {
    const product = this.getCurrentProduct();
    const variant = this.getCurrentProductDefaultVariant();
    const { price, compare_at_price } = variant;
    const { label, handle } = product.swatch;

    this.setColor(label, handle);
    this.setPrice(price, compare_at_price);
    this.updateSellingPlanToggle(product, variant);
  }

  /**
   * Updates swatch states, CTA visibility, and messaging based on the selected size.
   * @param {boolean} isFirstLoad Indicates whether the initialization path should skip enabling inputs.
   */
  updateProducts() {
    const size = this.getSize();
    const sizeLabel = this.getSizeLabel();
    this.setSuggestionMessageForUnavailableVariant();

    this.products.forEach((p) => {
      let { handle: swatchHandle, label: colorLabel } = p.swatch;
      let variant = p.variants.find((v) => v.size_handle === size);
      let colorInput = this.color.querySelector(
        `input[value="${swatchHandle}"]`
      );

      if (!variant) {
        this.toggleInputDisable(colorInput, true);
        if (colorInput.checked) {
          this.setUnavailableVariantMessage(colorLabel, sizeLabel);
          this.toggleButtonDisplay(this.unavailableVariantBtn, true);
          this.toggleButtonDisplay(this.klaviyoBtn, false);
          this.toggleButtonDisplay(this.atcBtn, false);
          this.setSuggestionMessageForUnavailableVariant(true);
        }
        return;
      }

      if (colorInput.checked) {
        this.updateInstallmentVariant(variant.id);
      }

      if (
        variant.is_inventory_tracked &&
        (!variant.available || variant.inventory <= 0)
      ) {
        this.toggleInputOOS(colorInput, "", true);
        if (colorInput.checked) {
          this.toggleButtonDisplay(this.klaviyoBtn, true);
          this.toggleButtonDisplay(this.atcBtn, false);
          this.toggleButtonDisplay(this.unavailableVariantBtn, false);
        }
        return;
      }

      if (variant.available || !variant.is_inventory_tracked) {
        this.toggleInputDisable(colorInput, false);
        this.toggleInputOOS(colorInput, "", false);
        if (colorInput.checked) {
          this.toggleButtonDisplay(this.atcBtn, true);
          this.toggleButtonDisplay(this.unavailableVariantBtn, false);
          this.toggleButtonDisplay(this.klaviyoBtn, false);
          this.setPrice(variant.price, variant.compare_at_price);
          this.updateSellingPlanToggle(p, variant);
        }
      }
    });
  }

  /**
   * Handles size selector changes by updating the label and refreshing availability states.
   * @param {Event} event Input event from the size selector.
   */
  sizeEventHandler(event) {
    const input = event.target.closest("input");
    if (!input) return;

    const { size } = input.dataset;
    this.sizeLabel.innerHTML = size;
    this.updateProducts();
  }

  /**
   * Handles color selector changes by updating the gallery and product state.
   * @param {Event} event Input event from the color selector.
   */
  colorEventHandler(event) {
    const input = event.target.closest("input");
    if (!input) return;

    const { productCaseHandle, title } = input.dataset;

    this.setColor(title);
    this.updateGallery(productCaseHandle);
    this.updateProducts();
  }

  /**
   * Handles Add to Cart button clicks by adding the currently selected variant.
   * @param {Event} event Click event triggered by the button.
   */
  atcBtnEventHandler(event) {
    const btn = event.target.closest("button");
    if (!btn) return;
    const size = this.getSize();
    const { productCaseHandle } =
      this.color.querySelector(`input:checked`).dataset;
    const variantId = this.products
      .find((p) => p.handle === productCaseHandle)
      .variants.find((v) => v.size_handle === size).id;

    const sellingPlanRadio = this.querySelector(
      'input[name="pillow-case-selling-plan"]:checked'
    );
    let sellingPlanId = null;
    if (
      sellingPlanRadio &&
      sellingPlanRadio.value === "subscription" &&
      this.currentSellingPlanId
    ) {
      sellingPlanId = this.currentSellingPlanId;
    }

    this.addToCart(variantId, sellingPlanId);
  }

  /**
   * Dispatches the Klaviyo subscription payload for the current selection.
   */
  dispatchKlaviyoSubscriptionData() {
    const detail = this.getKlaviyoSubscriptionDetail();
    this.dispatchEventWithDetail("pdp-klaviyo-subscription", detail);
  }

  /**
   * Reads the serialized body pillow case data from the DOM.
   * @returns {Array}
   */
  getBodyPillowCaseData() {
    let data = JSON.parse(
      document.querySelector("#body-pillow-case-data").textContent
    );
    return data.cases;
  }

  /**
   * Retrieves the product marked as current within the serialized cases.
   * @returns {Object|undefined} Current product entry if found.
   */
  getCurrentProduct() {
    return this.products.find((p) => p.current_product);
  }

  /**
   * Retrieves the variant flagged as default for the current product.
   * @returns {Object|undefined} Default variant entry if present.
   */
  getCurrentProductDefaultVariant() {
    const currentProduct = this.getCurrentProduct();
    return currentProduct.variants.find(
      (v) => v.current_product_default_variant
    );
  }

  /**
   * Retrieves the currently selected size option handle.
   * @returns {string} Selected size handle.
   */
  getSize() {
    return this.size.querySelector(`input:checked`).value;
  }

  /**
   * Retrieves the selected size label from the checked input.
   * @returns {string} Selected size label.
   */
  getSizeLabel() {
    return this.size.querySelector("input:checked").dataset.size;
  }

  /**
   * Retrieves the selected color label from the checked input.
   * @returns {string} Selected color label.
   */
  getColorLabel() {
    return this.color.querySelector("input:checked").dataset.title;
  }

  /**
   * Retrieves the product handle from the checked color option.
   * @returns {string} Selected product handle.
   */
  getProductHandle() {
    return this.color.querySelector("input:checked").dataset.productCaseHandle;
  }

  /**
   * Builds the selected product details used for Klaviyo subscription tracking.
   * @returns {{title: string, size: string, color: string, variantId: number|string}} Selected product detail payload.
   */
  getKlaviyoSubscriptionDetail() {
    const productHandle = this.getProductHandle();
    const selectedSize = this.getSize();
    const product = this.products.find((p) => p.handle === productHandle);
    const variant = product.variants.find(
      (v) => v.size_handle === selectedSize
    );

    const { title: pTitle } = product;
    const { size: vSize, id: vId } = variant;
    return {
      title: pTitle,
      size: vSize,
      color: this.getColorLabel(),
      variantId: vId,
    };
  }

  /**
   * Sets and renders the default size for the current product variant.
   */
  setDefaultSize() {
    const variant = this.getCurrentProductDefaultVariant();
    const { size, size_handle } = variant;
    this.setSize(size, size_handle);
  }

  /**
   * Updates the size label and optionally checks a matching input by handle.
   * @param {string} size
   * @param {string} size_handle
   */
  setSize(size, size_handle) {
    this.sizeLabel.innerHTML = size;
    if (size_handle !== "") {
      const sizeInput = this.size.querySelector(
        `input[value="${size_handle}"]`
      );
      this.toggleInputCheck(sizeInput, true);
    }
  }

  /**
   * Updates the color label and optionally checks a matching swatch by handle.
   * @param {string} label
   * @param {string} handle
   */
  setColor(label = "", handle = "") {
    this.colorLabel.innerHTML = label;
    if (handle !== "") {
      const input = this.color.querySelector(`input[value="${handle}"]`);
      this.toggleInputCheck(input, true);
    }
  }

  /**
   * Renders price and compare-at price within the CTA.
   * @param {string} price
   * @param {string} compare_at_price
   */
  setPrice(price, compare_at_price) {
    this.price.innerHTML = `
      ${price} ${compare_at_price !== "" ? `<s>${compare_at_price}</s>` : ``}
      `;
  }

  /**
   * Updates the payment terms forms owned by this product section.
   * @param {number|string} variantId Selected case variant ID
   */
  updateInstallmentVariant(variantId) {
    const productInfo = this.closest("product-info");
    if (!productInfo || variantId == null) return;

    productInfo
      .querySelectorAll('form[id^="product-form-installment-"]')
      .forEach((installmentForm) => {
        const input = installmentForm.querySelector('input[name="id"]');
        if (!input) return;

        input.value = variantId;
        installmentForm
          .querySelectorAll("shopify-payment-terms")
          .forEach((paymentTerms) => {
            paymentTerms.setAttribute("variant-id", variantId);
          });
        input.dispatchEvent(new Event("change", { bubbles: true }));
      });
  }

  /**
   * Sets the unavailable variant message for the CTA button.
   * @param {string} color Variant color name
   * @param {string} size Variant size description
   */
  setUnavailableVariantMessage(color = "", size = "") {
    this.unavailableVariantBtn.innerHTML = `<span>The ${color} color doesn't come in ${size} size</span>`;
  }

  /**
   * Shows a suggestion pointing to the first available color for the current size.
   * @param {boolean} showSuggestion Whether to display the suggestion message.
   */
  setSuggestionMessageForUnavailableVariant(showSuggestion = false) {
    const size = this.getSizeLabel();
    const firstAvailableColor = this.color.querySelector(
      `input:not(:disabled,.oos-variant)`
    );

    if (!showSuggestion || !firstAvailableColor) {
      this.suggestionMessageForUnavailableVariant.style.display = "none";
      return;
    }
    const messageContainer =
      this.suggestionMessageForUnavailableVariant.querySelector("span");

    let { title } = firstAvailableColor.dataset;

    messageContainer.innerHTML = `The ${title} color is available in ${size} size`;
    this.suggestionMessageForUnavailableVariant.style.display = "inline-flex";
  }

  /**
   * Toggles the input's checked state and ariaChecked flag.
   * @param {HTMLInputElement} input
   * @param {boolean} [isChecked=false]
   */
  toggleInputCheck(input, isChecked = false) {
    input.checked = isChecked;
    input.ariaChecked = isChecked;
  }

  /**
   * Toggles the input's disabled state and ariaDisabled flag.
   * @param {HTMLInputElement} input
   * @param {boolean} [isDisabled=false]
   */
  toggleInputDisable(input, isDisabled = false) {
    input.disabled = isDisabled;
    input.ariaDisabled = isDisabled;
  }

  /**
   * Toggles the out-of-stock class on an input element.
   * @param {HTMLInputElement} input
   * @param {string} inputId HTML input id
   * @param {boolean} [isOutOfStock=false]
   */
  toggleInputOOS(input, inputId = "", isOutOfStock = false) {
    if (input !== "") {
      input.classList[isOutOfStock ? "add" : "remove"]("oos-variant");
      return;
    }
    if (inputId !== "") {
      const input = this.querySelector(`input#${inputId}`);
      if (!input) return;
      input.classList[isOutOfStock ? "add" : "remove"]("oos-variant");
      return;
    }
  }

  /**
   * Toggles the button CSS display property
   * @param {HTMLButtonElement} button
   * @param {boolean} showBtn
   */
  toggleButtonDisplay(button, showBtn = true) {
    button.style.display = showBtn ? "flex" : "none";
  }

  /**
   * Enables or disables a button and mirrors the state in aria attributes.
   * @param {HTMLButtonElement} button
   * @param {boolean} [isDisabled=false]
   */
  toggleButtonDisabled(button, isDisabled = false) {
    button.disabled = isDisabled;
    button.ariaDisabled = isDisabled;
  }

  /**
   * Shows or hides the selling plan toggle and updates subscription pricing.
   * @param {Object} product - The current case product data.
   * @param {Object} variant - The current variant data.
   */
  updateSellingPlanToggle(product, variant) {
    if (!this.shopliftSubscriptionAllowed) {
      this.sellingPlanToggle.style.display = "none";
      this.currentSellingPlanId = null;
      return;
    }

    if (
      !product.selling_plan_groups ||
      product.selling_plan_groups.length === 0 ||
      !variant.selling_plan_allocations ||
      variant.selling_plan_allocations.length === 0
    ) {
      this.sellingPlanToggle.style.display = "none";
      this.currentSellingPlanId = null;
      return;
    }

    this.sellingPlanToggle.style.display = "block";

    // Prefer the Aktiva-Connect-owned plan group when the product is
    // associated with both the legacy orphan group and the managed one
    // (migration window). Identify the right group by its inner plan ID
    // (stable, unhashed in product.json) — Shopify obfuscates group IDs
    // in the storefront-facing JSON, so we can't compare against the
    // group's own ID. Fall back to first group if the managed plan
    // isn't on this product (shouldn't happen post-cutover).
    const MANAGED_PLAN_ID = "3437527175";
    const managedGroup = product.selling_plan_groups.find((g) =>
      g.plans?.some((p) => String(p.id) === MANAGED_PLAN_ID),
    );
    const chosenGroup = managedGroup || product.selling_plan_groups[0];
    const firstPlan = managedGroup
      ? managedGroup.plans.find((p) => String(p.id) === MANAGED_PLAN_ID)
      : chosenGroup.plans[0];
    const allocation = variant.selling_plan_allocations.find(
      (a) => a.selling_plan_id === firstPlan.id
    );

    if (allocation) {
      const strikePrice = variant.compare_at_price && variant.compare_at_price !== variant.price
        ? variant.compare_at_price
        : variant.price;
      this.subscriptionPriceLabel.innerHTML = `<span class="sp-compare-price">${strikePrice}</span>${allocation.per_delivery_price}`;
      const hasCompare = variant.compare_at_price && variant.compare_at_price !== variant.price;
      this.onetimePriceLabel.innerHTML = hasCompare
        ? `<span class="sp-compare-price">${variant.compare_at_price}</span>${variant.price}`
        : variant.price;
      this.currentSellingPlanId = firstPlan.id;
    }

    // Update ATC price if subscription is currently selected
    const selectedRadio = this.querySelector(
      'input[name="pillow-case-selling-plan"]:checked'
    );
    if (selectedRadio && selectedRadio.value === "subscription" && allocation) {
      this.setPrice(allocation.per_delivery_price, variant.price);
    }
  }

  /**
   * Handles selling plan radio toggle changes and updates the ATC price display.
   * @param {Event} event - The change event from the selling plan radio.
   */
  sellingPlanChangeHandler(event) {
    const selectedValue = event.target.value;
    const size = this.getSize();
    const { productCaseHandle } =
      this.color.querySelector("input:checked").dataset;
    const product = this.products.find((p) => p.handle === productCaseHandle);
    const variant = product.variants.find((v) => v.size_handle === size);

    if (
      selectedValue === "subscription" &&
      variant.selling_plan_allocations &&
      variant.selling_plan_allocations.length > 0
    ) {
      const allocation = variant.selling_plan_allocations[0];
      this.setPrice(allocation.per_delivery_price, variant.price);
    } else {
      this.setPrice(variant.price, variant.compare_at_price);
    }
  }

  /**
   * Notifies listeners when the product handle changes so the gallery can update.
   * @param {string} productHandle
   */
  updateGallery(productHandle) {
    let colorEvent = new CustomEvent("body-pillow-case::product-change", {
      detail: {
        handle: productHandle,
      },
    });
    document.dispatchEvent(colorEvent);
  }

  /**
   * Posts the selected variant to the cart endpoint and refreshes the cart drawer.
   * @param {string} variantId
   * @param {string|null} sellingPlanId
   */
  async addToCart(variantId, sellingPlanId = null) {
    this.toggleButtonDisabled(this.atcBtn, true);
    const item = {
      id: variantId,
      quantity: 1,
    };
    if (sellingPlanId) {
      item.selling_plan = sellingPlanId;
    }
    const formData = {
      items: [item],
    };
    try {
      await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
    } catch (error) {
      console.log(`Template Body Pillow Case | ${error}`);
    } finally {
      this.openAndPopulateCart();
      this.toggleButtonDisabled(this.atcBtn, false);
    }
  }

  /**
   * Fetches the latest cart markup and updates the drawer UI.
   */
  async openAndPopulateCart() {
    // Fetch the updated cart drawer content from the server
    const response = await fetch("/cart");
    const responseText = await response.text();

    // Parse the fetched HTML response into a DOM document
    const newCartMarkup = new DOMParser().parseFromString(
      responseText,
      "text/html"
    );

    // Update cart icon bubble
    const newCartIconBubbleMarkup =
      newCartMarkup.querySelector("#cart-icon-bubble");
    this.cartCountBubble.innerHTML = newCartIconBubbleMarkup.innerHTML;

    // Remove empty class
    this.cartDrawer.classList.remove("is-empty");

    // Get new cart drawer markup
    const newCartDrawerMarkup = newCartMarkup.querySelector("#CartDrawer");

    // Replace markup
    this.cartDrawer
      .querySelector("#CartDrawer")
      .replaceWith(newCartDrawerMarkup);

    // Open cart
    this.cartDrawer.open();

    this.cartDrawer.hideInstallmentsCTA();
  }

  /**
   * Dispatches a bubbling CustomEvent from the document with the provided detail payload.
   * @param {string} eventName - Name of the event to dispatch.
   * @param {*} detail - Data to include in the event detail.
   */
  dispatchEventWithDetail(eventName, detail) {
    const event = new CustomEvent(eventName, {
      bubbles: true,
      detail,
    });

    document.dispatchEvent(event);
  }
}

customElements.define("body-pillow-case-block", BodyPillowCaseBlock);
